/**
 * Founder scoring-interview hooks (Phase 88 UI data foundation).
 *
 * `useInterview()` — start / send / complete mutations over the `interview-agent`
 * edge fn, accumulating the visible transcript. Follows the CLAUDE.md state rules:
 * React Query `useMutation` (no manual isLoading), centralized `handleError` in
 * every `onError`.
 *
 * `useInterviewSession(sessionId)` — polls the founder-private `interview_sessions`
 * row for `score_event_after`. This is THE mechanism the completion reveal (88-05)
 * depends on: `complete` returns immediately with `rescoreStatus:'processing'` and
 * NEVER carries the after-score (87-06); the re-score pins `score_event_after`
 * asynchronously, and this query self-stops polling once it lands.
 */

import { useState, useCallback } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { supabase, isSupabaseConfigured } from '@/lib/supabase/client';
import { mode } from '@/lib/config/app-mode';
import { handleError } from '@/lib/errors';
import {
  startInterview,
  sendInterviewMessage,
  completeInterview,
  generateDigest,
} from '@/lib/supabase/edge-functions';
import type {
  InterviewMessage,
  InterviewStartResponse,
  InterviewMessageResponse,
  InterviewCompleteResponse,
  InterviewDigestResponse,
} from './types';

/** The digest shape persisted on `interview_sessions.digest_json` (Task 2.2). */
export type StoredDigest = Pick<
  InterviewDigestResponse,
  'proseDigest' | 'aboutDraft' | 'publicSafeFields'
>;

// interview_sessions isn't in the generated types union here; cast for .from().
const db = supabase as any;

/** Poll cadence while a completed session awaits its background re-score. */
const RESCORE_POLL_INTERVAL_MS = 3000;

export const interviewKeys = {
  session: (sessionId: string) => ['interview', 'session', sessionId] as const,
  latestDigest: (startupId: string) => ['interview', 'latest-digest', startupId] as const,
};

/** Stable client-side id for optimistic user turns (list keys / rendering). */
function generateMessageId(): string {
  return `imsg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

// =============================================================================
// useInterview — start / send / complete + accumulated transcript
// =============================================================================

export function useInterview() {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<InterviewMessage[]>([]);

  const start = useMutation({
    mutationFn: async (params: { startupId: string }): Promise<InterviewStartResponse> => {
      const { data, error } = await startInterview(params);
      if (error || !data) throw new Error(error ?? 'Failed to start the conversation');
      return data;
    },
    onSuccess: (data) => {
      setSessionId(data.sessionId);
      // `messages` is the opener (new session) or the full transcript (resume).
      setMessages(data.messages);
    },
    onError: (err) => {
      handleError(err, { context: 'useInterview.start' });
    },
  });

  const send = useMutation({
    mutationFn: async (params: { content: string }): Promise<InterviewMessageResponse> => {
      if (!sessionId) throw new Error('No active interview session');
      const { data, error } = await sendInterviewMessage({ sessionId, content: params.content });
      if (error || !data) throw new Error(error ?? 'Failed to send your answer');
      return data;
    },
    onMutate: (params) => {
      // Optimistically show the founder's own turn so the UI never feels stalled.
      const userMessage: InterviewMessage = {
        role: 'user',
        content: params.content,
        id: generateMessageId(),
      };
      setMessages((prev) => [...prev, userMessage]);
    },
    onSuccess: (data) => {
      setMessages((prev) => [...prev, data.reply]);
    },
    onError: (err) => {
      handleError(err, { context: 'useInterview.send' });
    },
  });

  const complete = useMutation({
    mutationFn: async (): Promise<InterviewCompleteResponse> => {
      if (!sessionId) throw new Error('No active interview session');
      const { data, error } = await completeInterview({ sessionId });
      if (error || !data) throw new Error(error ?? 'Failed to finish the conversation');
      return data;
    },
    onError: (err) => {
      handleError(err, { context: 'useInterview.complete' });
    },
  });

  // Build the factual digest for wrap-up review (prose + About draft + allowlist
  // public-safe field proposals). Read-only preview — publishing is a separate,
  // founder-approved step (Task 2.3 review screen → publishPublicSafeFields).
  const digest = useMutation({
    mutationFn: async (): Promise<InterviewDigestResponse> => {
      if (!sessionId) throw new Error('No active interview session');
      const { data, error } = await generateDigest({ sessionId });
      if (error || !data) throw new Error(error ?? 'Failed to build your digest');
      return data;
    },
    onError: (err) => {
      handleError(err, { context: 'useInterview.digest' });
    },
  });

  // Destructure the (stable) reset methods so the useCallback deps don't carry
  // the whole, referentially-unstable mutation objects (react-query lint rule).
  const { reset: resetStart } = start;
  const { reset: resetSend } = send;
  const { reset: resetComplete } = complete;
  const { reset: resetDigest } = digest;

  const reset = useCallback(() => {
    setSessionId(null);
    setMessages([]);
    resetStart();
    resetSend();
    resetComplete();
    resetDigest();
  }, [resetStart, resetSend, resetComplete, resetDigest]);

  // Freshest aggregate interview-progress fraction: the latest turn's value,
  // falling back to the opener's. Aggregate-only — never a per-dimension ratio
  // (Task 1.3 trade-secret contract). `null` until the first response lands.
  const overallCoverage = send.data?.overallCoverage ??
    start.data?.overallCoverage ?? null;

  // Freshest per-dimension coverage markers (Task 2.1): latest turn's, falling
  // back to the opener's. 3-state qualitative only (label + captured/light/not_yet).
  const dimensionCoverage = send.data?.dimensionCoverage ??
    start.data?.dimensionCoverage ?? [];

  return {
    sessionId,
    messages,
    start,
    send,
    complete,
    digest,
    reset,
    overallCoverage,
    dimensionCoverage,
  };
}

// =============================================================================
// useInterviewSession — poll for the background re-score's after-event
// =============================================================================

export interface InterviewSessionState {
  status: string | null;
  scoreEventBefore: string | null;
  scoreEventAfter: string | null;
  /**
   * True while the session is completed but its re-score after-event has not yet
   * been pinned. Loading-gate correctness (CLAUDE.md P0-4 spirit): a null
   * after-event during the loading/processing window must NOT be read as "no
   * uplift" — it means "still computing". Consumers gate the reveal on this.
   */
  isAwaitingRescore: boolean;
}

export interface InterviewSessionRow {
  status: string | null;
  score_event_before: string | null;
  score_event_after: string | null;
}

export function mapSessionRow(row: InterviewSessionRow): InterviewSessionState {
  const scoreEventAfter = row.score_event_after ?? null;
  return {
    status: row.status ?? null,
    scoreEventBefore: row.score_event_before ?? null,
    scoreEventAfter,
    isAwaitingRescore: row.status === 'completed' && scoreEventAfter === null,
  };
}

/**
 * The single source of the poll decision (also used by `refetchInterval`): keep
 * polling ONLY while a completed session is still awaiting its after-event, and
 * return `false` the instant `score_event_after` is present — no perpetual poll.
 */
export function interviewSessionPollInterval(
  state: InterviewSessionState | undefined,
): number | false {
  return state?.isAwaitingRescore ? RESCORE_POLL_INTERVAL_MS : false;
}

const EMPTY_SESSION_ROW: InterviewSessionRow = {
  status: null,
  score_event_before: null,
  score_event_after: null,
};

/**
 * Read + poll the founder-private `interview_sessions` row (RLS-scoped to the
 * owner). Polls ONLY while a completed session still lacks `score_event_after`,
 * and STOPS the instant the after-event is pinned.
 */
export function useInterviewSession(sessionId: string | undefined) {
  return useQuery({
    queryKey: interviewKeys.session(sessionId || ''),
    queryFn: async (): Promise<InterviewSessionState> => {
      if (!sessionId || mode.isMock || !isSupabaseConfigured()) {
        return mapSessionRow(EMPTY_SESSION_ROW);
      }
      const { data, error } = await db
        .from('interview_sessions')
        .select('status, score_event_before, score_event_after')
        .eq('id', sessionId)
        .maybeSingle();
      if (error) throw error;
      return mapSessionRow((data as InterviewSessionRow | null) ?? EMPTY_SESSION_ROW);
    },
    enabled: !!sessionId,
    // Poll while awaiting the background re-score; self-stop once it lands.
    refetchInterval: (query) =>
      interviewSessionPollInterval(query.state.data as InterviewSessionState | undefined),
  });
}

// =============================================================================
// useLatestSessionDigest — re-open the stored digest for later publish (Task 2.3)
// =============================================================================

export interface LatestSessionDigest {
  sessionId: string;
  digest: StoredDigest;
}

/**
 * Narrow the raw `digest_json` blob to a usable {@link StoredDigest}. Returns
 * null unless it carries the two narrative strings the review needs; the
 * public-safe proposals default to `{}` so a partial blob still opens cleanly.
 */
export function mapStoredDigest(raw: unknown): StoredDigest | null {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Record<string, unknown>;
  const proseDigest = typeof d.proseDigest === 'string' ? d.proseDigest : null;
  const aboutDraft = typeof d.aboutDraft === 'string' ? d.aboutDraft : null;
  if (proseDigest === null && aboutDraft === null) return null;
  const publicSafeFields =
    d.publicSafeFields && typeof d.publicSafeFields === 'object'
      ? (d.publicSafeFields as Record<string, string>)
      : {};
  return {
    proseDigest: proseDigest ?? '',
    aboutDraft: aboutDraft ?? '',
    publicSafeFields,
  };
}

/**
 * Read the founder's most recent stored interview digest for a startup so it can
 * be re-opened and published later WITHOUT re-interviewing (spec §3.3). Reads the
 * latest `interview_sessions` row that has a persisted `digest_json` (RLS-scoped
 * to the owner). Returns null when there is no stored digest yet.
 */
export function useLatestSessionDigest(startupId: string | undefined) {
  return useQuery({
    queryKey: interviewKeys.latestDigest(startupId || ''),
    queryFn: async (): Promise<LatestSessionDigest | null> => {
      if (!startupId || mode.isMock || !isSupabaseConfigured()) return null;
      const { data, error } = await db
        .from('interview_sessions')
        .select('id, digest_json, started_at')
        .eq('startup_id', startupId)
        .not('digest_json', 'is', null)
        .order('started_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      const row = data as { id: string; digest_json: unknown } | null;
      if (!row) return null;
      const digest = mapStoredDigest(row.digest_json);
      return digest ? { sessionId: row.id, digest } : null;
    },
    enabled: !!startupId,
    staleTime: 60 * 1000,
  });
}
